import { useQuery } from '@tanstack/react-query';
import { Phone } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { StatCard } from '@/components/ui/stat-card';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { api } from '@/lib/api';
import { useFilters } from '@/lib/filters';
import { MetaShell, Toggle } from './MetaShell';

/*
 * Meta → Kunder. One row per person who came in through a Meta lead form:
 * which ad, which form, whether they booked (our calendar or Momence), whether
 * they showed up, whether they became a member. Same identity and outcome logic
 * as the Leads tab (server: dashboard/meta-customers.ts).
 */

type Data = Awaited<ReturnType<typeof api.metaCustomers>>;
type Row = Data['people'][number];

const DASH = '—';
const whenFmt = new Intl.DateTimeFormat('nb-NO', {
  timeZone: 'Europe/Oslo',
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});
const fmt = (iso: string | null | undefined) => {
  if (!iso) return DASH;
  try {
    return whenFmt.format(new Date(iso));
  } catch {
    return iso.slice(0, 16);
  }
};

/** «Moss || FLAGSHIP • V8 28 Stille • video» → «V8 28 Stille» — the part a person reads. */
function shortAd(name: string | null): string | null {
  if (!name) return null;
  const parts = name
    .split('•')
    .map((s) => s.trim())
    .filter(Boolean);
  if (parts.length >= 3) return parts.slice(1, -1).join(' • ');
  if (parts.length === 2) return parts[1] ?? name;
  return name.replace(/^Moss\s*\|\|\s*/, '');
}
/** «Meta | Free Trial | Moss - v4 - Egen kalender (nono.no)» → «v4 · Egen kalender (nono.no)». */
function shortForm(name: string | null): string | null {
  if (!name) return null;
  const m = /(v\d+)\s*-\s*(.+)$/i.exec(name);
  return m ? `${m[1]} · ${m[2]}` : name;
}

type Filter = 'alle' | 'booket' | 'ikke';

export function MetaCustomersPage() {
  const { from, to } = useFilters();
  const query = useQuery({
    queryKey: ['meta-customers', from, to],
    queryFn: () => api.metaCustomers({ from, to }),
  });
  const [filter, setFilter] = useState<Filter>('alle');

  const rows = useMemo(() => {
    const all = query.data?.people ?? [];
    if (filter === 'booket') return all.filter((r) => r.booking && !r.booking.cancelled);
    if (filter === 'ikke') return all.filter((r) => !r.booking || r.booking.cancelled);
    return all;
  }, [query.data, filter]);

  return (
    <MetaShell
      title="Kunder fra Meta"
      lead={
        <>
          Én rad per person som har fylt ut et lead-skjema på Facebook eller Instagram: hvilken
          annonse, hvilket skjema, om de booket, om de møtte opp og om de ble medlem. Styres av
          datovelgeren øverst.
        </>
      }
      aside={
        <Toggle
          value={filter}
          onChange={setFilter}
          options={[
            { value: 'alle', label: 'Alle' },
            { value: 'booket', label: 'Booket' },
            { value: 'ikke', label: 'Ikke booket' },
          ]}
        />
      }
    >
      {query.isLoading ? (
        <LoadingState />
      ) : query.isError || !query.data ? (
        <ErrorState onRetry={() => query.refetch()} />
      ) : query.data.totals.people === 0 ? (
        <EmptyState title="Ingen Meta-leads i perioden">
          Velg en lengre periode i datovelgeren.
        </EmptyState>
      ) : (
        <>
          <Totals t={query.data.totals} />
          <Card className="overflow-x-auto p-0">
            <table className="w-full min-w-[960px] text-body">
              <thead>
                <tr className="border-b border-border text-left text-label text-fg-muted">
                  <th className="px-3 py-2 font-medium">Person</th>
                  <th className="px-3 py-2 font-medium">Lead</th>
                  <th className="px-3 py-2 font-medium">Annonse</th>
                  <th className="px-3 py-2 font-medium">Skjema</th>
                  <th className="px-3 py-2 font-medium">Booking</th>
                  <th className="px-3 py-2 font-medium">Oppmøte</th>
                  <th className="px-3 py-2 font-medium">Medlem</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <CustomerRow key={r.key} r={r} />
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}
    </MetaShell>
  );
}

function Totals({ t }: { t: Data['totals'] }) {
  const pct = (n: number) => (t.people ? `${Math.round((n / t.people) * 100)} %` : DASH);
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      <StatCard label="Personer" value={t.people} source="lead" sub="fylte ut skjemaet" />
      <StatCard
        label="Booket"
        value={t.booked}
        source="booking"
        sub={`${pct(t.booked)} · ${t.bookedNono} selv på nono.no · ${t.bookedMomence} i Momence`}
      />
      <StatCard
        label="Møtte opp"
        value={t.showed}
        source="momence"
        sub={`${pct(t.showed)} av alle`}
      />
      <StatCard
        label="Ble medlem"
        value={t.members}
        source="momence"
        sub={`${pct(t.members)} av alle`}
      />
    </div>
  );
}

function CustomerRow({ r }: { r: Row }) {
  const ad = shortAd(r.adName);
  const form = shortForm(r.formName);
  return (
    <tr className="border-b border-border/60 align-top last:border-0">
      <td className="px-3 py-2">
        <div className="font-medium text-fg">{r.name}</div>
        {r.phone ? (
          <a
            href={`tel:${r.phone}`}
            className="inline-flex items-center gap-1 text-label text-fg-muted hover:text-fg"
          >
            <Phone className="size-3" aria-hidden />
            {r.phone}
          </a>
        ) : null}
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-fg-muted">{fmt(r.leadAt)}</td>
      <td className="px-3 py-2">
        <div className="text-fg" title={r.adName ?? r.adId ?? undefined}>
          {ad ?? (r.adId ? `Annonse ${r.adId}` : 'Ukjent (organisk eller test)')}
        </div>
        {r.campaignName ? (
          <div className="text-label text-fg-muted">
            {shortAd(r.campaignName) ?? r.campaignName}
          </div>
        ) : null}
      </td>
      <td className="px-3 py-2 text-fg-muted" title={r.formName ?? r.formId ?? undefined}>
        {form ?? r.formId ?? DASH}
      </td>
      <td className="px-3 py-2">
        {r.booking ? (
          <div className="flex flex-col gap-1">
            <div className="flex flex-wrap items-center gap-1">
              <Badge tone={r.booking.via === 'nono' ? 'booking' : 'neutral'}>
                {r.booking.via === 'nono' ? 'Selv på nono.no' : 'Momence'}
              </Badge>
              {r.booking.cancelled ? <Badge tone="bad">Avlyst</Badge> : null}
            </div>
            <div className="text-label text-fg-muted">
              Time {fmt(r.booking.slotAt)} · booket{' '}
              {r.booking.hoursToBook < 1
                ? 'samme time'
                : `etter ${Math.round(r.booking.hoursToBook)} t`}
            </div>
          </div>
        ) : (
          <span className="text-fg-muted">Ikke booket</span>
        )}
      </td>
      <td className="px-3 py-2">
        {r.showed === 'yes' ? (
          <div className="flex flex-col gap-1">
            <Badge tone="good">Møtte opp</Badge>
            <span className="text-label text-fg-muted">{fmt(r.showedAt)}</span>
          </div>
        ) : r.showed === 'upcoming' ? (
          <Badge tone="neutral">Kommer</Badge>
        ) : r.showed === 'no' ? (
          <Badge tone="warn">Ikke sjekket inn</Badge>
        ) : (
          <span className="text-fg-muted">{DASH}</span>
        )}
      </td>
      <td className="px-3 py-2">
        {r.member ? <Badge tone="good">Medlem</Badge> : <span className="text-fg-muted">Nei</span>}
      </td>
    </tr>
  );
}
